<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Klienci</span>
        <h3 class="page-title">Lista klientów</h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          class="text-nowrap"
          @click.prevent="$router.push('/klienci/nowy')">
          <i class="material-icons">add</i>
          Dodaj klienta
        </d-button>
      </d-col>
    </d-row>

    <d-row
      class="d-flex ml-0 mb-3 page-filters w-100">
      <validation-observer
        ref="customers-filters-form"
        tag="form"
        style="width: 100%; gap: 0.5rem;"
        class="d-flex">
        <d-input
          placeholder="Podaj nazwę firmy"
          v-model="filters.search" />
        <div :class="{ 'form-control': true, 'is-select': true }">
          <v-select
            :options="statusOptions"
            :reduce="type => type.value"
            v-model="filters.is_archived"
            placeholder="Status klienta"
            :clearable="false"
            :searchable="false"
            label="text">
          </v-select>
        </div>
        <d-button
          theme="info"
          outline
          class="text-nowrap"
          @click.prevent="validateFiltersForm">Szukaj</d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">Osoba kontaktowa</th>
            <th
              scope="col"
              class="border-0">Dane firmy</th>
            <th
              scope="col"
              class="border-0 rwd-disabled">Adres</th>
            <th
              scope="col"
              :class="{
                'border-0 text-right column-sortable': true,
                'is-active': sorting.column && sorting.column === 'dept',
                'is-up': sorting.direction && sorting.column === 'dept' && sorting.direction === 'ASC'
              }"
              @click.prevent="sortBy('dept')"
              style="cursor: pointer;">
              Wysokość zadłużenia
            </th>
            <th
              scope="col"
              :class="{
                'border-0 text-right column-sortable': true,
                'is-active': sorting.column && sorting.column === 'first_dept_order_weeks',
                'is-up': sorting.direction && sorting.column === 'first_dept_order_weeks' && sorting.direction === 'ASC'
              }"
              @click.prevent="sortBy('first_dept_order_weeks')"
              style="cursor: pointer;">
              Liczba tygodni
            </th>
            <th
              scope="col"
              class="border-0 text-center">Akcje</th>
          </tr>
        </thead>
        <tbody v-if="loaded && customers">
          <tr
            v-for="(customer, index) of customers"
            :key="'user-' + index">
            <td class="align-middle">
              {{ customer.name }}
              <span class="bottom-text">
                Telefon: <span class="bottom-text-value-green">{{ customer.phone }}</span>
              </span>
              <span class="bottom-text">
                E-mail: <span class="bottom-text-value">{{ customer.email }}</span>
              </span>
            </td>
            <td class="align-middle">
              {{ customer.invoice_name }}
              <span class="bottom-text">NIP:
                <span
                  v-if="customer.invoice_nip"
                  class="bottom-text-value-green">
                  {{ customer.invoice_nip }}
                </span>
                <span
                  v-if="!customer.invoice_nip"
                  class="text-secondary">
                  Brak
                </span>
                <div class="rwd-tr-active">
                  <span class="bottom-text text-nowrap">
                    Miasto:
                    <span
                      v-if="customer.invoice_city"
                      class="bottom-text-value-green">
                      {{ customer.invoice_city }}
                    </span>
                    <span
                      v-if="!customer.invoice_city"
                      class="text-secondary">
                      Brak
                    </span>
                  </span>
                  <span class="bottom-text">
                    Kod pocztowy:
                    <span
                      v-if="customer.invoice_post_code"
                      class="bottom-text-value-green">
                      {{ customer.invoice_post_code }}
                    </span>
                    <span
                      v-if="!customer.invoice_post_code"
                      class="text-secondary">
                      Brak
                    </span>
                  </span>
                  <span class="bottom-text">
                    Ulica:
                    <span
                      v-if="customer.invoice_address"
                      class="bottom-text-value-green">
                      {{ customer.invoice_address }}
                    </span>
                    <span
                      v-if="!customer.invoice_address"
                      class="text-secondary">
                      Brak
                    </span>
                  </span>
                </div>
              </span>
            </td>
            <td class="align-middle rwd-disabled">
              <span class="bottom-text text-nowrap">
                Miasto:
                <span
                  v-if="customer.invoice_city"
                  class="bottom-text-value-green">
                  {{ customer.invoice_city }}
                </span>
                <span
                  v-if="!customer.invoice_city"
                  class="text-secondary">
                  Brak
                </span>
              </span>
              <span class="bottom-text">
                Kod pocztowy:
                <span
                  v-if="customer.invoice_post_code"
                  class="bottom-text-value-green">
                  {{ customer.invoice_post_code }}
                </span>
                <span
                  v-if="!customer.invoice_post_code"
                  class="text-secondary">
                  Brak
                </span>
              </span>
              <span class="bottom-text">
                Ulica:
                <span
                  v-if="customer.invoice_address"
                  class="bottom-text-value-green">
                  {{ customer.invoice_address }}
                </span>
                <span
                  v-if="!customer.invoice_address"
                  class="text-secondary">
                  Brak
                </span>
              </span>
            </td>
            <td class="align-middle text-right">
              <template v-if="customer.dept">
                <span class="text-danger">{{ customer.dept }}</span>
              </template>
              <template v-else>
                <span class="text-success">0</span>
              </template>
            </td>
            <td class="align-middle text-right">
              {{ customer.first_dept_order_weeks ? customer.first_dept_order_weeks : '-'}}
            </td>
            <td
              class="align-middle"
              style="width: 132px;">
              <div
                class="d-flex flex-wrap justify-content-end"
                style="gap: 8px;">
                <d-button
                  v-if="customer.order_url"
                  outline
                  class="action-button"
                  theme="info"
                  @click.prevent="openModalSendForm(customer.id, customer.order_url)"
                  title="Formularz zamówienia">
                  <i class="material-icons">message</i>
                </d-button>
                <d-button
                  theme="primary"
                  @click.native="$router.push('/klienci/edycja/' + customer.id)"
                  class="action-button"
                  title="Edytuj">
                  <i class="material-icons">edit</i>
                </d-button>
                <d-button
                  v-if="!customer.is_archived"
                  theme="warning"
                  class="action-button"
                  @click.native="archiveCustomers(customer.id)"
                  :disabled="archive_in_progress"
                  title="Archiwizuj">
                  <i class="material-icons">archive</i>
                </d-button>
                <d-button
                  theme="danger"
                  class="action-button"
                  @click.native="removeCustomers(customer.id)"
                  title="Usuń">
                  <i class="material-icons">delete</i>
                </d-button>
              </div>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="6"
              class="text-center">
              Brak klientów do wyświetlenia&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!loaded || !customers"
        class="pb-3 pt-3 text-center">
        Trwa ładowanie danych&hellip;
      </div>

      <d-alert
        v-if="load_error"
        show
        theme="warning">
        Wczytywanie danych nie powiodło się. <a
          href="javascript:window.location.reload();"
        class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
      </d-alert>
    </d-card>

    <customer-send-form
      :close-modal="closeModalSendForm"
      :customer-id="modal_send_form_id"
      :customer-url="modal_send_form_url"
      :show-modal="modal_send_form"/>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage"/>
  </d-container>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash.debounce';
import FormUtils from '../../utils/FormUtils.js';
import HasFilters from '../../mixins/HasFilters.vue';
import HasPagination from '../../mixins/HasPagination.vue';
import HasSorting from '../../mixins/HasSorting.vue';
import ListUtils from '../../utils/ListUtils.js';
import Pagination from '../../components/common/Pagination.vue';
import CustomerSendForm from './CustomerSendForm';

export default {
  name: 'customers',
  mixins: [
    HasFilters,
    HasPagination,
    HasSorting
  ],
  components: {
    CustomerSendForm,
    'pagination': Pagination
  },
  data () {
    return {
      customers: [],
      loaded: false,
      load_error: false,
      archive_in_progress: false,
      remove_in_progress: false,
      modal_send_form: false,
      modal_send_form_id: '',
      modal_send_form_url: '',
      filters: {
        search: '',
        is_archived: 0
      },
      sorting: {
        column: '',
        direction: ''
      },
      statusOptions: [
        {
          text: 'Klienci aktywni',
          value: 0
        },
        {
          text: 'Klienci archiwalni',
          value: 1
        },
      ]
    };
  },
  mounted () {
    if (!this.filters.is_archived) {
      this.filters.is_archived = 0;
    }
    this.loadFilteredData();
  },
  methods: {
    openModalSendForm (id, url) {
      this.modal_send_form = true;
      this.modal_send_form_id = id;
      this.modal_send_form_url = url;
    },
    closeModalSendForm () {
      this.modal_send_form = false;
      this.modal_send_form_id = '';
      this.modal_send_form_url = '';
    },
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.$bus.$emit('view-sorting-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {
        search: this.filters.search,
        is_archived: this.filters.is_archived || 0
      };

      let order = this.sorting;

      ListUtils.loadItemsData(this, {
        endpoint: '/customers/items?page=' + this.currentPage,
        listField: 'customers',
        errorKey: 'load_error',
        pagination: true,
        params: {
          where,
          order
        }
      });
    },
    clearFilters () {
      this.loaded = false;
      this.load_error = false;
      this.filters = {
        search: '',
        is_archived: 0
      };

      this.sorting = {
        column: '',
        direction: ''
      };

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('view-sorting-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['customers-filters-form'], this.debouncedLoadFilteredData);
    },
    archiveCustomers (userID) {
      this.archive_in_progress = true;

      Vue.swal({
        title: 'Czy na pewno chcesz zarchiwizować tego klienta?',
        text: 'Klient stanie się nieaktywny, ale będziesz mógł go później przywrócić.',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, archiwizuj',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          FormUtils.submit(this, {
            endpoint: '/customers/set-archived/' + userID,
            formData: { is_archived: 1 },
            successTitle: 'Zarchiwizowano klienta',
            successText: 'Wybrany klient został przeniesiony do archiwum.',
            errorTitle: 'Wystąpił błąd',
            errorText: 'Archiwizacja klienta nie powiodła się. Spróbuj ponownie.',
            successAction: () => {
              this.archive_in_progress = false;
              this.loadFilteredData();
            },
            failAction: () => {
              this.archive_in_progress = false;
            }
          });
        } else {
          this.archive_in_progress = false;
        }
      });
    },
    removeCustomers (userID) {
      this.remove_in_progress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/customers/delete/',
        id: userID,
        successTitle: 'Usunięto klienta',
        successText: 'Wybrany klient został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć tego klienta?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie klienta nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.remove_in_progress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.remove_in_progress = false;
        }
      });
    },
    sortBy (columnName) {
      let newDirection = 'DESC';

      if (this.sorting.column && this.sorting.column === columnName) {
        newDirection = this.sorting.direction === 'DESC' ? 'ASC' : 'DESC';
      }

      Vue.set(this, 'sorting', { column: columnName, direction: newDirection });
      this.debouncedLoadFilteredData();
    }
  }
};
</script>
<style lang="scss">
  .bottom-text {
    display: block;
    color: var(--text-gray-light);
    font-size: 0.9rem;
  &:first-child {
     margin-top: 0.4rem;
   }
  }
  .action-button {
    display: flex;
    .material-icons {
      left: 0;
      margin: auto;
      top: 0;
    }
  }
  .bottom-text-value-green {
    color: #17c671;
    font-weight: 500;
  }
  .bottom-text-value {
    font-weight: 500;
    color: var(--table-color) !important
  }
  .rwd-tr-active {
    display: none;
  }
  .column-sortable {
    padding-right: 20px!important;
    position: relative;

    &.is-active {
      &:after {
        opacity: 1;
      }

      &.is-up {
        &:after {
          top: calc(50% - 1px);
          transform: translateY(-50%) rotate(-180deg);
        }
      }
    }

    // &.is-up {
    //   &:after {
    //     top: calc(50% - 1px);
    //     transform: translateY(-50%) rotate(-180deg);
    //   }
    // }

    &:after {
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 7px solid var(--table-color);
      content: "";
      height: 0;
      opacity: 0.5;
      position: absolute;
      right: 5px;
      top: calc(50% + 2px);
      transform: translateY(-50%);
      transition:all .25s ease;
      width: 0;
    }
  }
  @media only screen and (max-width: 1280px) {
    .rwd-disabled {
      display: none;
    }
    .rwd-tr-active {
      display: block;
    }
  }
</style>
